/**
 * Editable accessibility statement content.
 *
 * The public /accessibility page renders this document; admins edit it in both
 * languages from /admin/accessibility-statement. The defaults below are the
 * shipped copy, used whenever no override row exists in `app_settings`.
 *
 * Body text is plain text: blank lines separate paragraphs, and lines starting
 * with "- " render as a bullet list.
 */

export type A11ySection = {
  id: string;
  titleEn: string;
  titleBn: string;
  bodyEn: string;
  bodyBn: string;
};

export type A11yStatementDoc = {
  updatedEn: string;
  updatedBn: string;
  introEn: string;
  introBn: string;
  sections: A11ySection[];
};

export const A11Y_STATEMENT_KEY = "accessibility_statement";

export const DEFAULT_A11Y_STATEMENT: A11yStatementDoc = {
  updatedEn: "9 July 2026",
  updatedBn: "৯ জুলাই ২০২৬",
  introEn:
    "Jantro Bazar is committed to making our marketplace usable by everyone in Bangladesh, including people who rely on assistive technologies.",
  introBn:
    "যন্ত্র বাজার বাংলাদেশের প্রত্যেক ব্যবহারকারীর জন্য মার্কেটপ্লেসকে সহজে ব্যবহারযোগ্য করতে প্রতিশ্রুতিবদ্ধ — সহায়ক প্রযুক্তির ব্যবহারকারীদের জন্যও।",
  sections: [
    {
      id: "commitment",
      titleEn: "Our commitment",
      titleBn: "আমাদের প্রতিশ্রুতি",
      bodyEn:
        "We aim to meet the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. Accessibility is part of every design review and release.",
      bodyBn:
        "আমরা WCAG 2.1 Level AA মেনে চলার লক্ষ্যে কাজ করি। প্রতিটি ডিজাইন রিভিউ ও রিলিজে অ্যাক্সেসিবিলিটি বিবেচনায় নেওয়া হয়।",
    },
    {
      id: "done",
      titleEn: "What we've done",
      titleBn: "আমরা যা করেছি",
      bodyEn: [
        "- Semantic HTML landmarks (header, main, nav, footer) on every page.",
        "- Full keyboard navigation with visible focus states.",
        "- Colour contrast tuned to WCAG AA for body text and UI.",
        "- Alt text on product photography and meaningful icons.",
        "- Bilingual UI — every screen is available in Bangla and English.",
        "- Respects the operating-system reduced-motion preference, and you can override it in your account settings.",
        "- Form fields have visible labels and inline error messages.",
      ].join("\n"),
      bodyBn: [
        "- প্রতিটি পেজে সেমান্টিক HTML ল্যান্ডমার্ক (header, main, nav, footer)।",
        "- কীবোর্ড দিয়ে সম্পূর্ণ নেভিগেশন, দৃশ্যমান ফোকাস স্টেট।",
        "- মূল টেক্সট ও UI-তে WCAG AA কালার কনট্রাস্ট।",
        "- প্রোডাক্ট ছবিতে ও অর্থপূর্ণ আইকনে alt টেক্সট।",
        "- দ্বিভাষিক UI — প্রতিটি স্ক্রিন বাংলা ও ইংরেজিতে উপলব্ধ।",
        "- অপারেটিং সিস্টেমের reduced-motion প্রেফারেন্স মানা হয়, অ্যাকাউন্ট সেটিংসেও বদলানো যায়।",
        "- ফর্ম ফিল্ডে দৃশ্যমান লেবেল ও ইনলাইন এরর মেসেজ।",
      ].join("\n"),
    },
    {
      id: "limitations",
      titleEn: "Known limitations",
      titleBn: "স্বীকৃত সীমাবদ্ধতা",
      bodyEn: [
        "- Some third-party embeds (courier tracking widgets, payment gateway iframes) are outside our control and may not fully conform.",
        "- Legacy PDF invoices generated before 2026 may lack full tag structure.",
        "- Seller-uploaded product descriptions vary in structure and may not always use headings correctly.",
      ].join("\n"),
      bodyBn: [
        "- কিছু থার্ড-পার্টি এমবেড (কুরিয়ার ট্র্যাকিং, পেমেন্ট গেটওয়ে iframe) আমাদের নিয়ন্ত্রণের বাইরে এবং সম্পূর্ণ কনফর্ম নাও হতে পারে।",
        "- ২০২৬-এর আগের PDF ইনভয়েসে সম্পূর্ণ ট্যাগ স্ট্রাকচার নাও থাকতে পারে।",
        "- বিক্রেতাদের আপলোড করা প্রোডাক্ট বিবরণে স্ট্রাকচার ভিন্ন হতে পারে।",
      ].join("\n"),
    },
    {
      id: "report",
      titleEn: "Report a barrier",
      titleBn: "সমস্যা জানান",
      bodyEn:
        "Found something you can't use? Please tell us — we treat accessibility bug reports as high priority.",
      bodyBn:
        "কোনো কিছু ব্যবহার করতে সমস্যা হচ্ছে? আমাদের জানান — অ্যাক্সেসিবিলিটি সংক্রান্ত রিপোর্ট আমাদের কাছে উচ্চ অগ্রাধিকার পায়।",
    },
  ],
};

const str = (v: unknown, fallback: string) =>
  typeof v === "string" && v.trim().length > 0 ? v : fallback;

/** Tolerant parse: anything malformed falls back to the shipped default. */
export function parseA11yStatement(value: unknown): A11yStatementDoc {
  const d = DEFAULT_A11Y_STATEMENT;
  if (!value || typeof value !== "object") return d;
  const raw = value as Record<string, unknown>;
  const sectionsRaw = Array.isArray(raw.sections) ? raw.sections : null;
  const sections: A11ySection[] = sectionsRaw
    ? sectionsRaw
        .filter((s): s is Record<string, unknown> => !!s && typeof s === "object")
        .map((s, i) => ({
          id: str(s.id, `section-${i + 1}`),
          titleEn: str(s.titleEn, ""),
          titleBn: str(s.titleBn, ""),
          bodyEn: typeof s.bodyEn === "string" ? s.bodyEn : "",
          bodyBn: typeof s.bodyBn === "string" ? s.bodyBn : "",
        }))
        .filter((s) => s.titleEn || s.titleBn || s.bodyEn || s.bodyBn)
    : d.sections;
  return {
    updatedEn: str(raw.updatedEn, d.updatedEn),
    updatedBn: str(raw.updatedBn, d.updatedBn),
    introEn: str(raw.introEn, d.introEn),
    introBn: str(raw.introBn, d.introBn),
    sections: sections.length ? sections : d.sections,
  };
}

/** Split a body into renderable blocks: bullet lists and paragraphs. */
export function statementBlocks(
  body: string,
): Array<{ kind: "p"; text: string } | { kind: "ul"; items: string[] }> {
  const out: Array<{ kind: "p"; text: string } | { kind: "ul"; items: string[] }> = [];
  const lines = body.split("\n");
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length) out.push({ kind: "ul", items: bullets });
    bullets = [];
  };
  for (const line of lines) {
    const t = line.trim();
    if (!t) {
      flush();
      continue;
    }
    if (t.startsWith("- ")) bullets.push(t.slice(2).trim());
    else {
      flush();
      out.push({ kind: "p", text: t });
    }
  }
  flush();
  return out;
}
